import { create } from "zustand";
import { BayData, SproutingData, BreakdownData } from "../Types/InspectionTypes";

const createEmptySprouting = (): SproutingData => ({
    none: false,
    controlled: false,
    eyesOpen: false,
    buds: false,
    peeping: false,
    lessThanHalfIn: false,
    greaterThanHalfIn: false,
});

const createEmptyBreakdown = (): BreakdownData => ({
    sporadic: false,
    mild: false,
    moderate: false,
    heavy: false,
    hotSpot: false,
});

const createEmptyBay = (bayName: string = '', bayId?: number): BayData => ({
    bay: bayName,
    bayId: bayId,
    topFront: '',
    bottomFront: '',
    frontAvgDeltaT: 0,
    topMiddle: '',
    bottomMiddle: '',
    middleAvgDeltaT: 0,
    topBack: '',
    bottomBack: '',
    backAvgDeltaT: 0,
    averageDeltaT: 0,
    avgSpDeltaT: 0,
    odors: 'none',
    bugs: 'none',
    ductLiquid: 'none',
    currentFillLevel: '',
    topPileConditions: '',
    sprouting: createEmptySprouting(),
    breakdown: createEmptyBreakdown(),
    notes: '',
});

interface BayObject {
    id: number;
    bayName: string;
}

interface BayStore {
    bays: BayData[];

    // Bay initialization (accepts either string[] or BayObject[])
    initializeBays: (bayData: string[] | BayObject[]) => void;

    // Bay field updates (only updates specific bay)
    updateBayField: <K extends keyof BayData>(bayIndex: number, field: K, value: BayData[K]) => void; 

    // Delta T calculations (only recalculates specific bay)
    calculateBayDeltaT: (bayIndex: number, plenumSetPoint?: number | '') => void;

    // Sprouting and Breakdown (only updates specific bay)
    updateBaySprouting: (bayIndex: number, field: keyof SproutingData, value: boolean) => void;
    updateBayBreakdown: (bayIndex: number, field: keyof BreakdownData, value: boolean) => void;

    // Bulk operations
    recalculateAllBaysAvgSpDeltaT: (plenumSetPoint: number | '') => void;
    setBays: (bays: BayData[]) => void;
    getBays: () => BayData[];
    resetBays: () => void;
}

export const useBayStore = create<BayStore>((set, get) => ({
    bays: [],

    initializeBays: (bayData) => {
        const bays = bayData.map(item => {
            if (typeof item === 'string') {
                // Legacy support for string arrays
                return createEmptyBay(item);
            } else {
                // New format with id and bayName
                return createEmptyBay(item.bayName, item.id);
            }
        });
        set({ bays });
    },

    updateBayField: (bayIndex, field, value) => {
        set((state) => {
            // Only update the specific bay, not the entire array
            const updatedBays = [...state.bays];
            updatedBays[bayIndex] = {
                ...updatedBays[bayIndex],
                [field]: value,
            };
            return { bays: updatedBays };
        });

        // If updating temperature fields, recalculate only this bay
        if (['topFront', 'bottomFront', 'topMiddle', 'bottomMiddle', 'topBack', 'bottomBack'].includes(field as string)) {
            // Use a small delay to batch calculations
            setTimeout(() => {
                get().calculateBayDeltaT(bayIndex);
            }, 0);
        }
    },

    calculateBayDeltaT: (bayIndex, plenumSetPoint) => {
        set((state) => {
            const bay = state.bays[bayIndex];
            if (!bay) return state;

            const updatedBay = { ...bay };

            // Calculate front average delta T
            if (bay.topFront !== '' && bay.bottomFront !== '') {
                updatedBay.frontAvgDeltaT = Number(bay.bottomFront) - Number(bay.topFront);
            } else {
                updatedBay.frontAvgDeltaT = 0;
            }

            // Calculate middle average delta T
            if (bay.topMiddle !== '' && bay.bottomMiddle !== '') {
                updatedBay.middleAvgDeltaT = Number(bay.bottomMiddle) - Number(bay.topMiddle);
            } else {
                updatedBay.middleAvgDeltaT = 0;
            }

            // Calculate back average delta T
            if (bay.topBack !== '' && bay.bottomBack !== '') {
                updatedBay.backAvgDeltaT = Number(bay.bottomBack) - Number(bay.topBack);
            } else {
                updatedBay.backAvgDeltaT = 0;
            }

            // Calculate average of all three delta Ts
            const validDeltas = [
                updatedBay.frontAvgDeltaT,
                updatedBay.middleAvgDeltaT,
                updatedBay.backAvgDeltaT
            ].filter(d => d !== 0);

            updatedBay.averageDeltaT = validDeltas.length > 0
                ? validDeltas.reduce((sum, val) => sum + val, 0) / validDeltas.length
                : 0;

            // Calculate avgSpDeltaT (average top temp - plenumSetPoint)
            const topTemps = [bay.topFront, bay.topMiddle, bay.topBack]
                .filter(t => t !== '')
                .map(t => Number(t));

            if (topTemps.length > 0 && plenumSetPoint !== undefined && plenumSetPoint !== '') {
                const avgTopTemp = topTemps.reduce((sum, val) => sum + val, 0) / topTemps.length;
                updatedBay.avgSpDeltaT = avgTopTemp - Number(plenumSetPoint);
            } else {
                updatedBay.avgSpDeltaT = 0;
            }

            // Only update the specific bay
            const updatedBays = [...state.bays];
            updatedBays[bayIndex] = updatedBay;

            return { bays: updatedBays };
        });
    },

    updateBaySprouting: (bayIndex, field, value) => {
        set((state) => {
            const updatedBays = [...state.bays];
            updatedBays[bayIndex] = {
                ...updatedBays[bayIndex],
                sprouting: {
                    ...updatedBays[bayIndex].sprouting,
                    [field]: value,
                },
            };
            return { bays: updatedBays };
        });
    },

    updateBayBreakdown: (bayIndex, field, value) => {
        set((state) => {
            const updatedBays = [...state.bays];
            updatedBays[bayIndex] = {
                ...updatedBays[bayIndex],
                breakdown: {
                    ...updatedBays[bayIndex].breakdown,
                    [field]: value,
                },
            };
            return { bays: updatedBays };
        });
    },

    recalculateAllBaysAvgSpDeltaT: (plenumSetPoint) => {
        const { bays } = get();
        bays.forEach((_, index) => {
            get().calculateBayDeltaT(index, plenumSetPoint);
        });
    },

    setBays: (bays) => {
        // Ensure all bays have calculated fields initialized
        const normalizedBays = bays.map((bay: any) => {
            // Check if sprouting/breakdown are already nested objects or flat fields
            const hasSproutingObject = bay.sprouting && typeof bay.sprouting === 'object';
            const hasBreakdownObject = bay.breakdown && typeof bay.breakdown === 'object';

            const normalized: BayData = {
                bay: bay.bay || '',
                bayId: bay.bayId,
                // Ensure all delta T fields exist
                frontAvgDeltaT: bay.frontAvgDeltaT ?? 0,
                middleAvgDeltaT: bay.middleAvgDeltaT ?? 0,
                backAvgDeltaT: bay.backAvgDeltaT ?? 0,
                averageDeltaT: bay.averageDeltaT ?? 0,
                avgSpDeltaT: bay.avgSpDeltaT ?? 0,
                // Ensure sprouting is a nested object
                sprouting: hasSproutingObject ? bay.sprouting : {
                    none: bay.none ?? false,
                    controlled: bay.controlled ?? false,
                    eyesOpen: bay.eyesOpen ?? false,
                    buds: bay.buds ?? false,
                    peeping: bay.peeping ?? false,
                    lessThanHalfIn: bay.lessThanHalfIn ?? false,
                    greaterThanHalfIn: bay.greaterThanHalfIn ?? false,
                },
                // Ensure breakdown is a nested object
                breakdown: hasBreakdownObject ? bay.breakdown : {
                    sporadic: bay.sporadic ?? false,
                    mild: bay.mild ?? false,
                    moderate: bay.moderate ?? false,
                    heavy: bay.heavy ?? false,
                    hotSpot: bay.hotSpot ?? false,
                },
                // Ensure other fields have proper defaults
                odors: (bay.odors || 'none') as BayData['odors'],
                bugs: (bay.bugs || 'none') as BayData['bugs'],
                ductLiquid: (bay.ductLiquid || 'none') as BayData['ductLiquid'],
                topPileConditions: (bay.topPileConditions || '') as BayData['topPileConditions'],
                currentFillLevel: bay.currentFillLevel ?? '',
                notes: bay.notes || '',
                // Convert null numeric values to empty strings
                topFront: bay.topFront ?? '',
                topMiddle: bay.topMiddle ?? '',
                topBack: bay.topBack ?? '',
                bottomFront: bay.bottomFront ?? '',
                bottomMiddle: bay.bottomMiddle ?? '',
                bottomBack: bay.bottomBack ?? '',
            };

            return normalized;
        });

        set({ bays: normalizedBays });

        // Recalculate delta T values for all bays
        normalizedBays.forEach((_, index) => {
            get().calculateBayDeltaT(index);
        });
    },

    getBays: () => {
        return get().bays;
    },

    resetBays: () => {
        set({ bays: [] });
    },
}));
